import { z } from 'zod';

import { isoDatetimeToDate } from '../codecs';
import { cameraMappingSchema } from './camera-mapping';
import { Vendor } from './vendor';

export type VideoIntegrationId = VideoIntegration['id'];

/** Everything an integration carries regardless of which vendor it talks to. */
const videoIntegrationBaseSchema = z.object({
  active: z.boolean(),
  cameras: z.array(cameraMappingSchema),
  createdAt: isoDatetimeToDate,
  error: z.record(z.string(), z.unknown()).optional(),
  id: z.string(),
  lastRun: isoDatetimeToDate,
  lastSynced: isoDatetimeToDate,
  name: z.string().optional(),
  teamId: z.string(),
  updatedAt: isoDatetimeToDate,
});

/** Vendors that hold no extra state of their own. */
const noMetadataSchema = z.object({});

export type FileUploadVideoIntegration = z.output<typeof fileUploadVideoIntegrationSchema>;
export type FileUploadVideoIntegrationDTO = z.input<typeof fileUploadVideoIntegrationSchema>;

export const fileUploadVideoIntegrationSchema = videoIntegrationBaseSchema.extend({
  meta: noMetadataSchema,
  vendor: z.literal(Vendor.FILE_UPLOAD),
});

export type LabpanoVideoIntegration = z.output<typeof labpanoVideoIntegrationSchema>;
export type LabpanoVideoIntegrationDTO = z.input<typeof labpanoVideoIntegrationSchema>;

export const labpanoVideoIntegrationSchema = videoIntegrationBaseSchema.extend({
  meta: noMetadataSchema,
  vendor: z.literal(Vendor.LABPANO),
});

export type PixellotVideoIntegration = z.output<typeof pixellotVideoIntegrationSchema>;
export type PixellotVideoIntegrationDTO = z.input<typeof pixellotVideoIntegrationSchema>;

export const pixellotVideoIntegrationSchema = videoIntegrationBaseSchema.extend({
  meta: z.object({ tenantName: z.string() }),
  vendor: z.literal(Vendor.PIXELLOT),
});

export type PlaySightVideoIntegration = z.output<typeof playSightVideoIntegrationSchema>;
export type PlaySightVideoIntegrationDTO = z.input<typeof playSightVideoIntegrationSchema>;

export const playSightVideoIntegrationSchema = videoIntegrationBaseSchema.extend({
  meta: z.object({ scopes: z.array(z.string()) }),
  vendor: z.literal(Vendor.PLAYSIGHT),
});

export type SaveMyPlayVideoIntegration = z.output<typeof saveMyPlayVideoIntegrationSchema>;
export type SaveMyPlayVideoIntegrationDTO = z.input<typeof saveMyPlayVideoIntegrationSchema>;

export const saveMyPlayVideoIntegrationSchema = videoIntegrationBaseSchema.extend({
  meta: noMetadataSchema,
  vendor: z.literal(Vendor.SAVE_MY_PLAY),
});

export type SynergyVideoIntegration = z.output<typeof synergyVideoIntegrationSchema>;
export type SynergyVideoIntegrationDTO = z.input<typeof synergyVideoIntegrationSchema>;

export const synergyVideoIntegrationSchema = videoIntegrationBaseSchema.extend({
  meta: z.object({ token: z.string(), tokenExpiresAt: isoDatetimeToDate }),
  vendor: z.literal(Vendor.SYNERGY),
});

export type UrlUploadVideoIntegration = z.output<typeof urlUploadVideoIntegrationSchema>;
export type UrlUploadVideoIntegrationDTO = z.input<typeof urlUploadVideoIntegrationSchema>;

export const urlUploadVideoIntegrationSchema = videoIntegrationBaseSchema.extend({
  meta: noMetadataSchema,
  vendor: z.literal(Vendor.URL_UPLOAD),
});

export type VideoIntegration = z.output<typeof videoIntegrationSchema>;
export type VideoIntegrationDTO = z.input<typeof videoIntegrationSchema>;

export const videoIntegrationSchema = z.discriminatedUnion('vendor', [
  fileUploadVideoIntegrationSchema,
  labpanoVideoIntegrationSchema,
  pixellotVideoIntegrationSchema,
  playSightVideoIntegrationSchema,
  saveMyPlayVideoIntegrationSchema,
  synergyVideoIntegrationSchema,
  urlUploadVideoIntegrationSchema,
]);

// *** Type Guards ***

export function isFileUploadVideoIntegration(
  integration: VideoIntegration,
): integration is FileUploadVideoIntegration {
  return integration.vendor === Vendor.FILE_UPLOAD;
}

export function isLabpanoVideoIntegration(
  integration: VideoIntegration,
): integration is LabpanoVideoIntegration {
  return integration.vendor === Vendor.LABPANO;
}

export function isPixellotVideoIntegration(
  integration: VideoIntegration,
): integration is PixellotVideoIntegration {
  return integration.vendor === Vendor.PIXELLOT;
}

export function isPlaySightVideoIntegration(
  integration: VideoIntegration,
): integration is PlaySightVideoIntegration {
  return integration.vendor === Vendor.PLAYSIGHT;
}

export function isSaveMyPlayVideoIntegration(
  integration: VideoIntegration,
): integration is SaveMyPlayVideoIntegration {
  return integration.vendor === Vendor.SAVE_MY_PLAY;
}

export function isSynergyVideoIntegration(
  integration: VideoIntegration,
): integration is SynergyVideoIntegration {
  return integration.vendor === Vendor.SYNERGY;
}

export function isUrlUploadVideoIntegration(
  integration: VideoIntegration,
): integration is UrlUploadVideoIntegration {
  return integration.vendor === Vendor.URL_UPLOAD;
}

// *** Utils ***

export const VideoIntegrationUtils = {
  /**
   * Whether a Synergy access token has run out and has to be pasted in again.
   *
   * Tokens without an `exp` claim are stored with an expiry far in the future rather than with
   * none at all, so this stays a plain comparison against now.
   */
  isSynergyTokenExpired(integration: Pick<SynergyVideoIntegration, 'meta'>): boolean {
    return integration.meta.tokenExpiresAt.getTime() < Date.now();
  },
  /**
   * Whether the integration has stopped working for a reason only a user can fix.
   *
   * Synergy is the only vendor with such a state today — its access token is issued outside of
   * this app and expires; every other vendor authenticates without anything to renew.
   */
  requiresAction(integration: VideoIntegration): boolean {
    return (
      isSynergyVideoIntegration(integration) &&
      VideoIntegrationUtils.isSynergyTokenExpired(integration)
    );
  },
};
